'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheck, faPencil, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

import { ListItemDropdown } from '../ListItemDropdown';
import { Input } from 'src/components';
import { IconButton } from 'src/components/PulseUI/IconButton';
import { useNotification } from 'src/context/notification';
import { useUpdateListMutation } from 'src/api/redux/lists/listsApi';
import type { ListItemProps } from './types';

const MAX_LIST_NAME_LENGTH = 50;

/**
 * ListItem component displays a single list item with edit capabilities
 * and selection state. It allows users to view, select, and edit list names.
 *
 * @param props - The component props
 * @returns A React component for displaying and managing a list item
 */
const ListItem: React.FC<ListItemProps> = ({
  list,
  isSelected = false,
  isShared = false,
  onClick,
}): React.ReactElement => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>(list.name);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { setNotification } = useNotification();
  const [updateList] = useUpdateListMutation();

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleEdit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      setIsEditing(true);
    },
    []
  );

  const handleCancel = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>): void => {
      e.stopPropagation();
      setNewName(list.name);
      setIsEditing(false);
    },
    [list.name]
  );

  const handleSubmit = useCallback(
    async (e?: React.FormEvent): Promise<void> => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      const trimmedName = newName.trim();

      if (!trimmedName) {
        setNotification({
          type: 'error',
          title: 'Invalid Input',
          message: 'List name cannot be empty',
        });
        return;
      }

      if (trimmedName === list.name) {
        setIsEditing(false);
        return;
      }

      try {
        setIsLoading(true);
        await updateList({
          listId: list.id,
          listName: trimmedName,
          ownerId: undefined,
          sharedWith: undefined,
        }).unwrap();

        setIsEditing(false);
        setNotification({
          type: 'success',
          title: 'Success',
          message: 'List name updated successfully',
        });
      } catch (error) {
        setNotification({
          type: 'error',
          title: 'Error',
          message: 'Failed to update list name',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [list.id, list.name, newName, setNotification, updateList]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Escape') {
        handleCancel(e as any);
      }
    },
    [handleCancel]
  );

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setNewName(e.target.value);
    },
    []
  );

  const handleFormClick = useCallback((e: React.MouseEvent): void => {
    e.stopPropagation();
  }, []);

  return (
    <div
      onClick={!isEditing ? onClick : undefined}
      className={twMerge(
        'group px-4 py-2 flex items-center justify-between gap-2',
        !isEditing && 'hover:bg-neutral-50 cursor-pointer',
        isSelected && 'bg-teal-50'
      )}
      role="option"
      aria-selected={isSelected}
      data-testid={`list-item-${list.id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <div className="min-w-0 flex-1 flex items-center gap-2">
          {isEditing ? (
            <form
              onSubmit={handleSubmit}
              className="flex-1 flex items-center gap-2"
              onClick={handleFormClick}
            >
              <Input
                ref={inputRef}
                value={newName}
                onChange={handleNameChange}
                disabled={isLoading}
                size="sm"
                className="w-full rounded-full"
                placeholder="Enter list name"
                aria-label="Edit list name"
                maxLength={MAX_LIST_NAME_LENGTH}
                onKeyDown={handleKeyDown}
                onBlur={handleSubmit}
                data-testid={`list-item-${list.id}-edit-input`}
              />
              <div className="flex items-center flex-shrink-0">
                <IconButton
                  icon={faCheck}
                  onClick={handleSubmit}
                  disabled={isLoading}
                  aria-label="Save list name"
                  size="sm"
                  circular
                  color="success"
                  data-testid={`list-item-${list.id}-save`}
                />
                <IconButton
                  icon={faXmark}
                  onClick={handleCancel}
                  disabled={isLoading}
                  aria-label="Cancel editing"
                  size="sm"
                  circular
                  color="danger"
                  data-testid={`list-item-${list.id}-cancel`}
                />
              </div>
            </form>
          ) : (
            <div className="flex items-center gap-1 min-w-0 max-w-full">
              <span className="text-sm text-neutral-700 whitespace-normal overflow-hidden min-w-0 max-w-[calc(100%-40px)]">
                {list.name}
              </span>
              {list.num_items > 0 && (
                <span className="text-xs text-neutral-500 flex-shrink-0 whitespace-nowrap mt-0.5">
                  ({list.num_items})
                </span>
              )}
              <IconButton
                icon={faPencil}
                onClick={!isShared ? handleEdit : undefined}
                aria-label="Edit list name"
                size="sm"
                variant="plain"
                circular
                className={twMerge(
                  'text-neutral-400 opacity-0 group-hover:opacity-100 transition-opacity duration-200',
                  isShared &&
                    'opacity-0 group-hover:opacity-0 pointer-events-none'
                )}
                data-testid={`list-item-${list.id}-edit`}
              />
            </div>
          )}
        </div>
        {isSelected && !isEditing && (
          <Icon
            icon={faCheck}
            className="text-teal-500 flex-shrink-0"
            size="sm"
            aria-hidden="true"
          />
        )}
      </div>
      {!isEditing && !isShared && (
        <div className="relative flex-shrink-0">
          <ListItemDropdown listId={list.id} />
        </div>
      )}
    </div>
  );
};

export default ListItem;
